
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { User, LogOut, LayoutDashboard, ShieldAlert } from 'lucide-react';
import { Grid2X2 } from 'lucide-react';
import { toast } from 'sonner';

interface UserMenuProps {
  userType: 'ahavexer' | 'customer' | 'admin';
  color?: string;
  className?: string;
}

const UserMenu: React.FC<UserMenuProps> = ({
  userType,
  color = 'currentColor',
  className,
}) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Successfully logged out');
    } catch (error) {
      console.error(error);
      toast.error('Failed to log out');
    }
  };
  
  const goToDashboard = () => {
    if (userType === 'admin') {
      navigate('/admin-dashboard');
    } else if (userType === 'ahavexer') {
      navigate('/ahavexer-dashboard');
    } else {
      navigate('/customer-dashboard');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          aria-label={`${userType} account menu`}
        >
          {userType === 'admin' ? (
            <ShieldAlert color={color} size={20} strokeWidth={1.5} />
          ) : userType === 'ahavexer' ? (
            <Grid2X2 color={color} size={20} strokeWidth={1.5} />
          ) : (
            <User color={color} size={20} strokeWidth={1.5} />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>
          {userType === 'admin' 
            ? 'Admin Account' 
            : userType === 'ahavexer' 
              ? 'Ahavexer Account' 
              : 'Customer Account'}
        </DropdownMenuLabel>
        <DropdownMenuLabel>{currentUser?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={goToDashboard}>
          <LayoutDashboard className="mr-2 h-4 w-4" />
          <span>Dashboard</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
